<script lang="ts">
export default {
  name: 'LoginTest',
  inheritAttrs: false,
  customOptions: {}
}
</script>

<script setup lang="ts">
import {nextTick, onMounted, ref, watch} from 'vue';
import {useGlobalStore} from "../stores/global";

const emit = defineEmits(['update:step']);
const props = defineProps({
  step: { type: String, required: true },
  verify_email_url: { type: String, required: true },
  login_url: { type: String, required: true },
  forgot_password_url: { type: String, default: '' },
  trans: { type: Object, required: true },
});

const store = useGlobalStore();

const emailInputRef = ref<HTMLElement | null>(null);
const newPasswordInputRef = ref<HTMLElement | null>(null);

const step = defineModel({ type: String, default: 'verify_email' });
onMounted(() => {
  emailInputRef.value?.focus();
});
watch(step, async (_new) => {
  emit('update:step', _new)
  switch (_new) {
    case 'login':
      await nextTick(() => {
        newPasswordInputRef.value?.focus();
      });
      break;
  }
});
let login_password = ref('');
let login_remember = ref(false);
let show_password = ref(false);

const saving = ref(false);
const error_msg = ref('');

const verifyEmail = async () => {
  error_msg.value = '';
  if (!store.user.email) {
    return;
  }
  if (saving.value) {
    return;
  }
  saving.value = true;

  const dt = await store.verifyEmail(props.verify_email_url);
  if (dt.can_login) {
    // email exists, user can login
    step.value = 'login';
  } else if (dt.success) {
    // user does not exist, email is valid, user can register new account
    step.value = 'register';
  } else {
    // email is invalid
    error_msg.value = dt.message;
  }

  saving.value = false;
};

const login = async () => {
  error_msg.value = '';

  if (!store.user.email || !login_password.value || saving.value) {
    return;
  }
  try {
    saving.value = true;
    const dt = await store.login(props.login_url, login_password.value, login_remember.value);
    if (dt.success) {
      step.value = 'success';
    } else {
      error_msg.value = dt.message || 'Login failed';
      // Workaround for 'Bad credentials.' message, not translateable via security.nl.xlf?
      if (dt.message == 'Bad credentials.') {
        error_msg.value = '' + props.trans.account.password_bad_credentials;
      }
      newPasswordInputRef.value?.focus();
    }
  } catch (error) {
    console.error('Error login:', error);
  } finally {
    saving.value = false;
  }
};

const next = async () => {
  switch (step.value) {
    case 'verify_email':
    case 'register':
      await verifyEmail();
      break;
    case 'login':
      await login(); break;
  }
}
const reset = () => {
  step.value = 'verify_email';
  store.user.email = '';
  login_password.value = '';
  error_msg.value = '';
  nextTick(() => {
    emailInputRef.value?.focus();
  });
};
const togglePasswordVisibility = async () => {
  show_password.value = !show_password.value;
  // await nextTick(); // Ensures DOM updates after the state change
};
</script>

<template>
  <template v-if="step === 'verify_email' || step === 'register'">
    <div class="row">
      <div>
        <label for="reaction-form-email">{{ trans.email }}</label>
        <input ref="emailInputRef" v-model="store.user.email" @keyup.enter="verifyEmail" id="reaction-form-email" type="email"
               placeholder="" required/>
      </div>
    </div>
  </template>
  <template v-else-if="step === 'login'">
    <div class="row">
      <div class="input-group">
        <label for="reaction-form-email">{{ trans.email }}</label>
        <input v-model="store.user.email" id="reaction-form-email" type="email" placeholder="" required readonly disabled />
        <a class="action reset" @click.prevent="reset" href="#">&times;</a>
      </div>
    </div>

    <div class="row">
      <div class="input-group">
        <label for="reaction-form-password">{{ trans.password }}</label>
        <input ref="newPasswordInputRef" v-model="login_password" @keyup.enter="login" id="reaction-form-password"
               :type="show_password ? 'text' : 'password'" autocomplete="new-password" placeholder="" required />
        <a class="action show" :class="show_password ? 'icon-eye-open' : 'icon-eye-closed'" @click.prevent="togglePasswordVisibility" href="#"></a>
      </div>
    </div>

    <label class="ui-checkbox" style="margin: -1em 0 0 0;">
      <input type="checkbox" v-model="login_remember" />
      <span>{{ trans.remember_me }}</span>
    </label>
  </template>

  <div ref="errorEl" v-if="error_msg" class="notice notice-error" v-html="error_msg"></div>

  <div class="form-controls">
    <a v-if="forgot_password_url && (step === 'login')" :href="forgot_password_url" class="icon-rarr">{{ trans.forgot_password }}</a>

    <span></span>

    <button class="btn-solid icon-rarr" @click.prevent="next" :disabled="saving">{{ trans.send }}</button>
  </div>
</template>